import { IntervalSet } from '../core/ranges';
import type { AppError, ByteSpan, PriorityClass, SessionSnapshot } from '../core/types';
import { OneFlowError } from '../core/errors';
import type { Engine } from './engine';

export type ReadResult =
  | { status: 'ok'; bytes: Uint8Array }
  /** Some bytes are not saved yet. Never a silent partial array. TYPE-020, ALG-068 */
  | { status: 'not-ready'; missing: ByteSpan[] };

interface Waiter {
  span: ByteSpan;
  resolve: () => void;
  reject: (e: unknown) => void;
  cleanup: () => void;
}

/**
 * The only way UI and parsers touch a session's bytes. Reads are served from
 * local storage; a missing range becomes a scheduler demand, never a fetch.
 * ARCH-004, ARCH-006, ALG-069
 */
export class SessionHandle {
  snapshot: SessionSnapshot | null = null;
  private readable = IntervalSet.empty;
  private waiters = new Set<Waiter>();
  private listeners = new Set<() => void>();
  private seekEpoch = 1;
  private nextDemandId = 1;
  private disposed = false;

  constructor(
    readonly id: string,
    private readonly engine: Engine,
  ) {}

  /** Called by the engine for each worker snapshot. */
  update(s: SessionSnapshot): void {
    if (this.disposed) return;
    this.snapshot = s;
    this.readable = IntervalSet.from(s.readable);
    for (const w of [...this.waiters]) {
      if (this.readable.covers(w.span)) {
        w.cleanup();
        w.resolve();
      } else if (s.state === 'fatalError' || s.state === 'recoverableError') {
        w.cleanup();
        w.reject(new OneFlowError(s.error?.code ?? 'UNKNOWN'));
      }
    }
    for (const l of [...this.listeners]) l();
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  get total(): number | null {
    return this.snapshot?.identity?.totalSize ?? null;
  }

  get isComplete(): boolean {
    return this.snapshot?.state === 'complete';
  }

  get seekable(): boolean {
    return this.snapshot?.seekable === true || this.isComplete;
  }

  get error(): AppError | null {
    return this.snapshot?.error ?? null;
  }

  readableSet(): IntervalSet {
    return this.readable;
  }

  isReadable(span: ByteSpan): boolean {
    return this.readable.covers(span);
  }

  /** Length of the saved run starting at `pos`. */
  contiguousFrom(pos: number): number {
    return this.readable.contiguousFrom(pos);
  }

  /** Start a new seek generation; stale demands and parser work are dropped. ALG-039 */
  beginSeekEpoch(): number {
    this.seekEpoch += 1;
    this.engine.cancelDemands(this.id, this.seekEpoch);
    return this.seekEpoch;
  }

  get epoch(): number {
    return this.seekEpoch;
  }

  /** Ask the scheduler for bytes. No fetch happens here. */
  demand(span: ByteSpan, priority: PriorityClass, reason: string, deadline?: number): void {
    const total = this.total;
    const end = total !== null ? Math.min(span.end, total) : span.end;
    if (end <= span.start) return;
    if (this.readable.covers({ start: span.start, end })) return;
    this.engine.sendDemand(this.id, {
      requestId: this.nextDemandId++,
      span: { start: span.start, end },
      priority,
      reason,
      epoch: this.seekEpoch,
      ...(deadline !== undefined ? { deadline } : {}),
    });
  }

  whenReadable(span: ByteSpan, signal?: AbortSignal): Promise<void> {
    if (this.readable.covers(span)) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      const onAbort = () => {
        waiter.cleanup();
        reject(new DOMException('aborted', 'AbortError'));
      };
      const waiter: Waiter = {
        span,
        resolve,
        reject,
        cleanup: () => {
          this.waiters.delete(waiter);
          signal?.removeEventListener('abort', onAbort);
        },
      };
      if (signal?.aborted) return onAbort();
      signal?.addEventListener('abort', onAbort, { once: true });
      this.waiters.add(waiter);
    });
  }

  /** Exact bytes if every byte is saved, otherwise the missing spans. TYPE-020 */
  async readAt(offset: number, length: number): Promise<ReadResult> {
    const total = this.total;
    const end = total !== null ? Math.min(offset + length, total) : offset + length;
    if (end <= offset) return { status: 'ok', bytes: new Uint8Array(0) };
    const want = { start: offset, end };
    if (!this.readable.covers(want))
      return { status: 'not-ready', missing: this.readable.missing(want) };
    const buf = await this.engine.readStorage(this.id, offset, end - offset);
    return { status: 'ok', bytes: new Uint8Array(buf) };
  }

  /** Demand, wait, then read. The standard path for parsers and viewers. */
  async ensure(
    span: ByteSpan,
    opts: { priority: PriorityClass; reason: string; signal?: AbortSignal; deadline?: number },
  ): Promise<Uint8Array> {
    this.demand(span, opts.priority, opts.reason, opts.deadline);
    const total = this.total;
    const end = total !== null ? Math.min(span.end, total) : span.end;
    await this.whenReadable({ start: span.start, end }, opts.signal);
    const r = await this.readAt(span.start, end - span.start);
    if (r.status !== 'ok') throw new OneFlowError('STORAGE_CORRUPT');
    return r.bytes;
  }

  dispose(): void {
    this.disposed = true;
    for (const w of [...this.waiters]) {
      w.cleanup();
      w.reject(new DOMException('session closed', 'AbortError'));
    }
    this.listeners.clear();
  }
}
